import { createFileRoute } from "@tanstack/react-router";
import { LegalPage } from "@/components/legal-page";
import { socialImageMeta, breadcrumbLd } from "@/lib/seo";

export const Route = createFileRoute("/legal/cookies")({
  head: () => ({
    meta: [
      { title: "Cookie Policy — BidNova Auctions" },
      { name: "description", content: "How BidNova Auctions uses cookies and similar technologies under PECR and UK GDPR." },
      { property: "og:title", content: "Cookie Policy — BidNova Auctions" },
      { property: "og:description", content: "The cookies we use and how to control them." },
      { property: "og:url", content: "https://bidnovaauctions.uk/legal/cookies" },
      { property: "og:type", content: "article" },
      ...socialImageMeta(),
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "https://bidnovaauctions.uk/legal/cookies" }],
    scripts: [breadcrumbLd([{ name: "Home", path: "/" }, { name: "Legal", path: "/legal/terms" }, { name: "Cookie Policy", path: "/legal/cookies" }])],
  }),
  component: CookiePage,
});

function CookiePage() {
  return (
    <LegalPage eyebrow="Legal" title="Cookie Policy" updated="7 July 2026">
      <p>
        This Cookie Policy explains how BidNova Auctions Ltd uses cookies
        and similar technologies on bidnovaauctions.uk. It should be
        read alongside our <a href="/legal/privacy">Privacy Policy</a>. Cookies
        are governed in the UK by the Privacy and Electronic Communications
        Regulations (PECR) and the UK GDPR.
      </p>

      <h2>1. What is a cookie?</h2>
      <p>
        A cookie is a small text file placed on your device when you visit a
        website. Cookies allow the site to remember your actions and
        preferences (such as being signed in) over a period of time.
      </p>

      <h2>2. Cookies we use</h2>

      <h3>Strictly necessary (no consent required)</h3>
      <ul>
        <li><strong>Session &amp; authentication:</strong> keep you signed in and secure your session.</li>
        <li><strong>Security tokens:</strong> protect against CSRF and abuse.</li>
        <li><strong>Load balancing:</strong> route your requests to the correct server.</li>
      </ul>

      <h3>Functional (used only where enabled)</h3>
      <ul>
        <li><strong>Preferences:</strong> remember display settings such as theme.</li>
        <li><strong>Watchlist state:</strong> remember which auctions you are watching between visits.</li>
      </ul>

      <h3>Analytics (opt-in)</h3>
      <ul>
        <li>Aggregate, privacy-respecting analytics that help us improve the Platform.</li>
        <li>These are only set after you consent via our cookie banner.</li>
      </ul>

      <h3>Third-party cookies</h3>
      <ul>
        <li><strong>Stripe:</strong> fraud prevention on checkout pages (strictly necessary for payments).</li>
        <li><strong>Google (if used):</strong> only when you choose "Continue with Google" to sign in.</li>
      </ul>

      <h2>3. Your choices</h2>
      <ul>
        <li>You can accept or reject non-essential cookies via our cookie banner.</li>
        <li>You can clear or block cookies through your browser settings; strictly necessary cookies cannot be disabled without breaking core features (bidding, checkout).</li>
        <li>You can change your preferences at any time by clearing site data and revisiting.</li>
      </ul>

      <h2>4. Do Not Track</h2>
      <p>
        Because there is no consistent industry standard for "Do Not Track"
        signals, our Platform does not currently respond to them.
      </p>

      <h2>5. Changes</h2>
      <p>We will update this policy when we change how we use cookies. Please check periodically.</p>

      <h2>6. Contact</h2>
      <p>
        Questions: <a href="mailto:privacy@bidnovaauctions.uk">privacy@bidnovaauctions.uk</a>.
      </p>
    </LegalPage>
  );
}
